import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    CacheCollection,
    CurrentRideContract,
    CurrentRideVinRequestContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { VehicleService } from '@polaris-customerweb/sharedcomponents.garage';
import { LogService } from '@polaris-customerweb/sharedcomponents.shared';
import {
    ConnectedVehicleService,
    VehiclePermissionService,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { memoize } from 'lodash-es';
import { Observable, firstValueFrom, forkJoin, of, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { InProgressRideResourceService } from './in-progress-ride-resource.service';
import { InProgressRideVm } from './in-progress-ride.vm';

@Injectable({ providedIn: 'root' })
export class InProgressRideService {
    readonly className = 'InProgressRideService';

    pollInProgressRides$ = memoize<
        (pollTime?: number) => Observable<InProgressRideVm[]>
    >((pollTime = this.pollTime) => {
        return timer(0, pollTime).pipe(
            switchMap(() => this.getInProgressRides()),
            shareReplay({
                bufferSize: 1,
                refCount: true,
                windowTime: this.pollTime,
            })
        );
    });

    private pollTime = 4000;
    private inProgressRidesCache = new CacheCollection<{
        vm: InProgressRideVm;
        lastUpdate: Date;
    }>();
    private allowedVinsCache: string[];
    private vinToIdMap = new Map<string, string>();

    constructor(
        private configService: ConfigService,
        private connectedVehicleService: ConnectedVehicleService,
        private logService: LogService,
        private resource: InProgressRideResourceService,
        private vehiclePermissionService: VehiclePermissionService,
        private vehicleService: VehicleService
    ) {}

    getInProgressRides(): Observable<InProgressRideVm[]> {
        return this.vehicleService.getOnBrandVehicleVins$().pipe(
            switchMap((vins) => {
                return this.getAllowedVins(vins);
            }),
            switchMap((vins) => {
                if (!vins || vins.length === 0) {
                    return of([] as CurrentRideContract[]);
                }

                const requestData: CurrentRideVinRequestContract[] = vins.map(
                    (vin) => {
                        const since =
                            this.inProgressRidesCache
                                .getItem(vin)
                                ?.lastUpdate?.toISOString() ?? '';
                        return { since, vin };
                    }
                );

                return this.resource.getInProgressRides(requestData);
            }),
            catchError((error) => {
                this.logService.warn(this.className, error);
                return of([] as CurrentRideContract[]);
            }),
            map((contracts) => {
                const rides = contracts.map((contract) => {
                    let previous = this.inProgressRidesCache.getItem(
                        contract.vin
                    );

                    if (
                        contract.resultStatus !== 'noLocations' &&
                        contract.resultStatus !== 'noRide' &&
                        contract.resultStatus !== 'success' &&
                        contract.resultStatus !== 'noVehicleAccess'
                    ) {
                        this.removeFromAllowedVins(contract?.vin || '');
                        this.inProgressRidesCache.clearItem(contract.vin);
                        this.vinToIdMap.delete(contract?.vin || '');
                    } else if (previous && previous.vm.id !== contract.tripId) {
                        previous = null;
                        this.inProgressRidesCache.clearItem(contract.vin);
                        this.vinToIdMap.delete(contract?.vin || '');
                    }

                    const updated = this.applyInProgressDelta(
                        contract,
                        previous?.vm
                    );
                    let lastUpdate: Date;
                    if (contract.lastUpdatedDate) {
                        lastUpdate = new Date(contract.lastUpdatedDate);
                    } else if (previous?.lastUpdate) {
                        lastUpdate = previous?.lastUpdate;
                    } else {
                        lastUpdate = new Date();
                    }

                    this.inProgressRidesCache.setItem(
                        {
                            lastUpdate,
                            vm: updated,
                        },
                        contract.vin
                    );
                    this.vinToIdMap.set(
                        contract.vin || '',
                        contract.tripId || ''
                    );

                    return updated;
                });
                return rides;
            })
        );
    }

    getInProgressRideByIdFromCache(id: string): InProgressRideVm {
        const vin = this.getVinForInProgressRide(id);
        if (!vin) {
            return new InProgressRideVm();
        }

        return this.getInProgressRideByVinFromCache(vin);
    }

    getInProgressRideByVinFromCache(vin: string): InProgressRideVm {
        const cached = this.inProgressRidesCache.getItem(vin);

        if (!cached) {
            return new InProgressRideVm();
        }

        return cached.vm;
    }

    async getUrlSegmentsForInProgressRide(id: string): Promise<string[]> {
        const vin = this.getVinForInProgressRide(id);
        const vehicle = await firstValueFrom(
            this.vehicleService.getVehicleByVin$(vin)
        );
        return ['vehicle', vehicle?.id || '', 'in-progress-ride', id];
    }

    saveInProgressRide(vin: string): Observable<void> {
        return this.resource.saveInProgressRide(vin);
    }

    discardInprogressRide(vin: string): Observable<void> {
        return this.resource.discardInProgressRide(vin);
    }

    inProgressRidesToGeoJson(
        inProgressRides: readonly InProgressRideVm[] = []
    ): GeoJSON.FeatureCollection {
        return {
            features: inProgressRides.map((ride) => ride.toGeoJson()),
            type: 'FeatureCollection',
        };
    }

    private applyInProgressDelta(
        contract: CurrentRideContract,
        vm?: InProgressRideVm
    ): InProgressRideVm {
        if (contract?.tripId !== vm?.id) {
            return InProgressRideVm.fromInProgressRideContract(contract);
        }

        if (
            contract?.resultStatus !== 'success' &&
            contract?.resultStatus !== 'noLocations'
        ) {
            return InProgressRideVm.fromInProgressRideContract(contract);
        }

        const newVm = InProgressRideVm.fromInProgressRideContract(contract);
        if (vm) {
            newVm.prependPreviousCoordinates(vm);
        }

        return newVm;
    }

    private getAllowedVins(vins: string[]): Observable<string[]> {
        if (this.allowedVinsCache) {
            return of(this.allowedVinsCache);
        }

        if (
            !vins ||
            vins.length === 0 ||
            !this.configService.featureFlags.isRideTrackingEnabled
        ) {
            this.allowedVinsCache = [];
            return of([] as string[]);
        }

        const isPollingAllowed$ = vins.reduce<
            Record<string, Observable<boolean>>
        >((settingsMap, vin) => {
            settingsMap[vin] = forkJoin([
                this.connectedVehicleService.isAutomaticRideTrackingEnabled(
                    vin
                ),
                this.vehiclePermissionService.canViewLocation(vin),
            ]).pipe(
                map(([rideTrackingEnabled, canViewLocation]) =>
                    Boolean(rideTrackingEnabled && canViewLocation)
                )
            );
            return settingsMap;
        }, {});

        return forkJoin(isPollingAllowed$).pipe(
            map((isPollingAllowed) => {
                this.allowedVinsCache = Object.keys(isPollingAllowed).filter(
                    (vin) => isPollingAllowed[vin]
                );

                return this.allowedVinsCache;
            })
        );
    }

    private getVinForInProgressRide(id: string): string {
        let vin = '';
        for (const [key, value] of this.vinToIdMap.entries()) {
            if (value === id) {
                vin = key;
                break;
            }
        }

        return vin;
    }

    private removeFromAllowedVins(vinToRemove: string): void {
        this.allowedVinsCache = this.allowedVinsCache.filter(
            (vin) => vin !== vinToRemove
        );
    }
}
